import { clampSkillLevel, languageLevelPercent, languageLevelText, resolveDesign } from '~/constants/cvDesign'
import type { CVData } from '~/types'

/**
 * What the three web CV templates (Studio, Classic, Modern) read from the CV
 * the same way the PDF does, so the page shows what the editor was told:
 *
 * - skill dots and language bars only when the design panel's "show levels"
 *   is on, from the same levels and the same reading of a language level as
 *   the PDF (constants/cvDesign.ts);
 * - dates as a short month and year in the CV's own language, with the CV's
 *   word for "present";
 * - a language level from the fixed scale as its CEFR code, or `native` as
 *   the CV's word for it.
 */
export function useWebCv(
  cvData: () => CVData | undefined,
  labels: () => { present: string; nativeSpeaker: string },
) {
  // `cvLanguage` is on the editor's CV type (types/cv.ts), not on the older one the templates take
  const language = computed(() => (cvData() as { cvLanguage?: string } | undefined)?.cvLanguage || 'cs')
  const showLevels = computed(() => resolveDesign(cvData()?.design).showLevels)

  /** 1–5, or 0 when levels are off or the skill has none. */
  function skillLevel(skill: string): number {
    if (!showLevels.value) return 0
    return clampSkillLevel(cvData()?.skillLevels?.[skill.trim()])
  }

  /** What the page prints for a language's level. */
  function languageLevel(level?: string): string {
    return languageLevelText(level, labels().nativeSpeaker)
  }

  /** 0–100 for the bar, 0 when levels are off or the level is not one we can read. */
  function languagePercent(level?: string): number {
    return showLevels.value ? languageLevelPercent(level) : 0
  }

  /**
   * Month + year in the CV's language. `Intl` throws on a language tag it does
   * not know, so an unknown one falls back to English rather than breaking
   * the whole page.
   */
  function formatMonth(value: string): string {
    const match = value.match(/^(\d{4})-(\d{1,2})/)
    const date = match ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)) : new Date(value)
    if (Number.isNaN(date.getTime())) return value
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', timeZone: 'UTC' }
    try {
      return new Intl.DateTimeFormat(language.value, options).format(date)
    } catch {
      return new Intl.DateTimeFormat('en-US', options).format(date)
    }
  }

  /** "Mar 2021 – Present"; a free-form `period` from older data wins. */
  function formatPeriod(item: { startDate?: string; endDate?: string; period?: string }): string {
    if (item.period) return item.period
    if (!item.startDate) return item.endDate ? formatMonth(item.endDate) : ''
    const start = formatMonth(item.startDate)
    return item.endDate ? `${start} – ${formatMonth(item.endDate)}` : `${start} – ${labels().present}`
  }

  /** A certificate's date is `YYYY-MM` from the editor or free text from an import. */
  function formatCertificateDate(value?: string): string {
    if (!value) return ''
    return /^\d{4}-\d{1,2}/.test(value.trim()) ? formatMonth(value.trim()) : value
  }

  return { showLevels, skillLevel, languageLevel, languagePercent, formatMonth, formatPeriod, formatCertificateDate }
}
